import Section from 'app/frontend/base/components/Section'
import {
  characterName,
  date,
  role,
  status,
  team,
} from 'app/frontend/base/config/column-definitions/application'
import { useUserApplications } from 'app/frontend/base/config/queries'
import { useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { DataTable } from '@/components/ui/data-table'
import { CircleCheck, CircleQuestionMark } from 'lucide-react'
import { ColumnDef } from '@tanstack/react-table'

const UserApplications = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const pendingColumns: ColumnDef<wowaudit.Application>[] = useMemo(
    () => [
      date,
      team,
      role({ withFilters: false }),
      characterName({ withFilters: false }),
      status({ withFilters: false }),
    ],
    [],
  )

  const processedColumns: ColumnDef<wowaudit.Application>[] = useMemo(
    () => [
      date,
      team,
      role({ withFilters: true }),
      characterName({ withFilters: true }),
      status({ withFilters: true }),
    ],
    [],
  )

  const { data: pendingApplications, isPending } = useUserApplications(true)
  const { data: processedApplications } = useUserApplications(false)

  const handleRowClick = (application: wowaudit.Application) => {
    navigate(`/profile/applications/${application.id}`, {
      state: {
        tableParams: Object.fromEntries(searchParams),
      },
    })
  }

  return (
    <Section title='Profile'>
      <h2 className='flex justify-between items-center'>
        <span className='flex items-center gap-3'>
          <span>My applications</span>
          <span className='bg-muted rounded-lg px-3 text-base'>
            {pendingApplications?.length}
          </span>
        </span>
      </h2>

      <div className='mb-6'>
        <div className='bg-muted rounded-md whitespace-nowrap'>
          <DataTable
            data={pendingApplications ?? []}
            columns={pendingColumns}
            includeHeader={true}
            emptyMessage={() => (
              <div
                className={`flex flex-col items-center gap-4 my-8 ${
                  isPending ? 'opacity-0' : ''
                }`}
              >
                {processedApplications?.length ? (
                  <>
                    <CircleCheck size={48} className='text-primary' />{' '}
                    <span>You have no pending applications</span>
                  </>
                ) : (
                  <>
                    <CircleQuestionMark size={48} className='text-blue-500' />{' '}
                    <span>You have never applied to any team on wowaudit</span>
                  </>
                )}
              </div>
            )}
            sortingState={[{ id: 'date', desc: true }]}
            getRowId={(application) => application.id.toString()}
            onRowClick={handleRowClick}
            rowClassName={() => `hover:bg-background`}
          />
        </div>
      </div>

      <div className='my-12'>
        {processedApplications?.length ? (
          <>
            <h2 className='text-xl font-semibold mb-3'>History</h2>
            <div className='bg-muted rounded-md whitespace-nowrap'>
              <DataTable
                data={processedApplications ?? []}
                columns={processedColumns}
                includeHeader={true}
                sortingState={[{ id: 'date', desc: true }]}
                getRowId={(application) => application.id.toString()}
                pagination={20}
                filterType='includesString'
                onRowClick={handleRowClick}
                rowClassName={() => 'hover:bg-background'}
              />
            </div>
          </>
        ) : null}
      </div>
    </Section>
  )
}

export default UserApplications
